/* eslint-disable react/jsx-indent, indent */
import './icon-fix.css';
import React from 'react';
import { CookiesProvider, Cookies } from 'react-cookie';
import { GqlClient, GqlContextProvider } from '@search/gql-client/src';

import {
    RouterProviderAppProps, RouterProvider
} from '@search/offer-search-routes/src';
import { ErrorCatcherProvider, ErrorCatcher } from '@search/error/src/catcher';
import { AuthInfoLegacy } from '@search/auth/src/AuthInfo';
import { M2AuthProvider } from '@search/auth/src/M2AuthProvider';
import { StaticPathProvider } from '@search/common-core/src/StaticPathContext';

import { GeoRootRegionProvider } from '@search/geo/src/GeoRootRegionProvider';
import type { GeoRootRegionRepository } from '@search/geo/src/GeoRootRegionRepository';
import { AgentFavoritesProvider } from '@search/classified-common/src/Agent/AgentFavoritesProvider/AgentFavoritesProvider';
import { DeveloperPromoConfigsProvider } from '../newbuilding/developer-promo/DeveloperPromoConfigsProvider';
import type { DeveloperPromoConfigs } from '../newbuilding/developer-promo/types';
import { VTBCoreYMapProvider } from '../../view/common/components/YMap/Provider';
import { GoogleOptimizeConfig, GoogleOptimizeProvider } from '../google/useGoogleOptimize';
import { FavoritesModalProvider } from '../../view/common/components/FavoritesButton/FavoritesModal';
import { BasicErrorProvider } from '../../view/common/components/FavoritesButton/BasicError';
import { ImageLazyObserverProvider } from '../image/lazy/ImageLazy';
import { SeoSearchUtm } from '../seo/SeoSearchUtm';

export type BootAppProviderProps = Pick<RouterProviderAppProps, 'router'> & {
    children?: React.ReactNode;
    auth?: AuthInfoLegacy;
    catcher?: ErrorCatcher;
    gql: GqlClient;
    googleOptimize?: GoogleOptimizeConfig;
    cookies?: Cookies;
    yMapsApiKey?: string;
    staticPath?: string;
    isMobile?: boolean;
    rootRegions: GeoRootRegionRepository;
    developerPromoConfigs: DeveloperPromoConfigs;
}

export function BootAppProvider(props: BootAppProviderProps) {
    SeoSearchUtm.init();

    return (
        <ErrorCatcherProvider options={props.catcher}>
            <GeoRootRegionProvider value={props.rootRegions}>
                <StaticPathProvider staticPath={props.staticPath}>
                    <GoogleOptimizeProvider value={props.googleOptimize}>
                        <CookiesProvider cookies={props.cookies}>
                            <M2AuthProvider auth={props.auth}>
                                <GqlContextProvider gql={props.gql}>
                                    <VTBCoreYMapProvider apiKey={props.yMapsApiKey}>
                                        <RouterProvider router={props.router}>
                                            <ImageLazyObserverProvider>
                                                <BasicErrorProvider isMobile={props.isMobile}>
                                                    <FavoritesModalProvider isMobile={props.isMobile}>
                                                        <DeveloperPromoConfigsProvider
                                                            developerPromoConfigs={props.developerPromoConfigs}
                                                        >
                                                            <React.Suspense fallback={null}>
                                                                {props.children}
                                                            </React.Suspense>
                                                        </DeveloperPromoConfigsProvider>
                                                    </FavoritesModalProvider>
                                                </BasicErrorProvider>
                                            </ImageLazyObserverProvider>
                                        </RouterProvider>
                                    </VTBCoreYMapProvider>
                                </GqlContextProvider>
                            </M2AuthProvider>
                        </CookiesProvider>
                    </GoogleOptimizeProvider>
                </StaticPathProvider>
            </GeoRootRegionProvider>
        </ErrorCatcherProvider>
    );
}
